import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Tag,
  Modal,
  Input,
  Space,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { internalAccounts } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { formatDateTime } from '../../utils/format';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import CopyableText from '../../components/internalAdmin/CopyableText';
import {
  AccountStatusTag,
  paiseToINR
} from '../../components/internalAdmin/AccountCells';

// Stage 5N-2B: tenant-account detail page for internal admins.
//
// Calls GET /api/internal/accounts/:tenantId. Renders three blocks:
//   1. Company details
//   2. Plan / payment
//   3. Approval status + actions
//
// Available actions depend on (a) the admin's role permissions and
// (b) the tenant's current accountStatus. The set:
//
//   pending_approval → Approve | Reject (reason)
//   active           → Suspend
//   suspended        → Reactivate
//   rejected         → no actions (immutable terminal state)

const { TextArea } = Input;

// paiseToINR + AccountStatusTag are imported from the shared
// AccountCells module so the formatting matches every list page.

// Render a registered_address JSONB blob as a single human-readable
// line. Empty parts are dropped, multiple parts joined by ", ". The
// snapshot is captured at signup time from the GST taxpayer record
// (pradr.addr) — fields are line1/line2/city/state/pincode/country.
// Returns '—' when the blob is missing or has no usable parts.
function formatAddress(address) {
  if (!address || typeof address !== 'object') return '—';
  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.pincode,
    address.country
  ]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter((p) => p.length > 0);
  return parts.length === 0 ? '—' : parts.join(', ');
}

export default function AccountDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [reactivateOpen, setReactivateOpen] = useState(false);
  // Stage 5N-10: SEZ approve/reject modal state.
  const [approveSezOpen, setApproveSezOpen] = useState(false);
  const [rejectSezOpen, setRejectSezOpen] = useState(false);
  const [rejectSezReason, setRejectSezReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await internalAccounts.get(tenantId);
      setAccount(data);
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load account' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const status = account ? account.accountStatus : null;
  const canApprove = hasPermission('accounts.approve');
  const canReject = hasPermission('accounts.reject');
  const canSuspend = hasPermission('accounts.suspend');
  const canReactivate = hasPermission('accounts.reactivate');
  // Stage 5N-10: SEZ-specific permissions. Granted to support_admin
  // and super_admin (see backend permissions.js).
  const canApproveSez = hasPermission('accounts.sez.approve');
  const canRejectSez = hasPermission('accounts.sez.reject');

  async function doApprove() {
    setBusy(true);
    try {
      const updated = await internalAccounts.approve(tenantId);
      setAccount(updated);
      setApproveOpen(false);
      notifySuccess('Account approved', `${updated.legalName || updated.slug} can now sign in.`);
    } catch (err) {
      notifyError(err, { title: 'Could not approve account' });
    } finally {
      setBusy(false);
    }
  }

  async function doReject() {
    const reason = rejectReason.trim();
    if (reason.length === 0) {
      notifyError(
        { code: 'VALIDATION_ERROR' },
        { title: 'Reason required', description: 'Enter a short reason for rejection.' }
      );
      return;
    }
    setBusy(true);
    try {
      const updated = await internalAccounts.reject(tenantId, { reason });
      setAccount(updated);
      setRejectOpen(false);
      setRejectReason('');
      notifySuccess('Account rejected', `${updated.legalName || updated.slug} marked rejected.`);
    } catch (err) {
      notifyError(err, { title: 'Could not reject account' });
    } finally {
      setBusy(false);
    }
  }

  async function doSuspend() {
    setBusy(true);
    try {
      const updated = await internalAccounts.suspend(tenantId, {
        reason: suspendReason.trim() || undefined
      });
      setAccount(updated);
      setSuspendOpen(false);
      setSuspendReason('');
      notifySuccess('Account suspended', `${updated.legalName || updated.slug} is now suspended.`);
    } catch (err) {
      notifyError(err, { title: 'Could not suspend account' });
    } finally {
      setBusy(false);
    }
  }

  async function doReactivate() {
    setBusy(true);
    try {
      const updated = await internalAccounts.reactivate(tenantId);
      setAccount(updated);
      setReactivateOpen(false);
      notifySuccess('Account reactivated', `${updated.legalName || updated.slug} is active again.`);
    } catch (err) {
      notifyError(err, { title: 'Could not reactivate account' });
    } finally {
      setBusy(false);
    }
  }

  async function doApproveSez() {
    setBusy(true);
    try {
      const updated = await internalAccounts.approveSez(tenantId);
      setAccount(updated);
      setApproveSezOpen(false);
      notifySuccess(
        'SEZ status approved',
        'Future renewal invoices for this account will be issued as zero-rated.'
      );
    } catch (err) {
      notifyError(err, { title: 'Could not approve SEZ status' });
    } finally {
      setBusy(false);
    }
  }

  async function doRejectSez() {
    const reason = rejectSezReason.trim();
    if (reason.length === 0) {
      notifyError(
        { code: 'VALIDATION_ERROR' },
        { title: 'Reason required', description: 'Enter a short reason for rejecting SEZ status.' }
      );
      return;
    }
    setBusy(true);
    try {
      const updated = await internalAccounts.rejectSez(tenantId, { reason });
      setAccount(updated);
      setRejectSezOpen(false);
      setRejectSezReason('');
      notifySuccess(
        'SEZ status rejected',
        'Billing will continue with regular GST.'
      );
    } catch (err) {
      notifyError(err, { title: 'Could not reject SEZ status' });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Account detail"
          actions={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
          }
        />
        <LoadingBlock tip="Loading account…" />
      </div>
    );
  }

  if (!account) {
    return (
      <div>
        <PageHeader
          title="Account detail"
          actions={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
          }
        />
        <EmptyState
          title="Account not found"
          description={
            error
              ? 'There was an error loading this account. It may have been deleted.'
              : 'No account matches that id.'
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3 flex-wrap">
            <span className="truncate">
              {account.legalName || account.displayName || account.slug}
            </span>
            <AccountStatusTag status={status} />
          </span>
        }
        subtitle={account.gstin ? `GSTIN ${account.gstin}` : 'No GSTIN on file'}
        actions={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
          </Space>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Company details" size="small">
          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="Legal name">
              {account.legalName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Display name">
              {account.displayName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="GSTIN">
              {account.gstin ? <code className="text-xs">{account.gstin}</code> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="State code">
              {account.stateCode || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Registered address">
              {formatAddress(account.registeredAddress)}
            </Descriptions.Item>
            <Descriptions.Item label="Slug">
              <CopyableText value={account.slug} max={0} />
            </Descriptions.Item>
            <Descriptions.Item label="Tenant ID">
              <CopyableText value={account.tenantId} max={14} />
            </Descriptions.Item>
            <Descriptions.Item label="Contact name">
              {account.contactName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Contact email">
              {account.contactEmail || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Contact mobile">
              {account.contactMobile || '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Plan & payment" size="small">
          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="Plan">
              <span className="capitalize">{account.planName || '—'}</span>
              {account.isTrial ? (
                <Tag color="gold" style={{ marginLeft: 6 }}>
                  trial
                </Tag>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label="Billing cycle">
              {account.billingCycle ? (
                <span className="capitalize">{account.billingCycle}</span>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Invoice number">
              {account.invoiceNumber ? (
                <Space size="small" wrap>
                  <code className="text-xs">{account.invoiceNumber}</code>
                  {account.invoiceId ? (
                    <Link to={`/internal/invoices/${account.invoiceId}/report`}>
                      <Button size="small" icon={<EyeOutlined />}>
                        View report
                      </Button>
                    </Link>
                  ) : null}
                </Space>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Invoice total">
              {paiseToINR(account.invoiceTotalPaise)}
            </Descriptions.Item>
            <Descriptions.Item label="Paid amount">
              {paiseToINR(account.paidAmountPaise)}
            </Descriptions.Item>
            <Descriptions.Item label="Razorpay payment id">
              {account.razorpayPaymentId ? (
                <CopyableText value={account.razorpayPaymentId} max={20} />
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Razorpay order id">
              {account.razorpayOrderId ? (
                <CopyableText value={account.razorpayOrderId} max={20} />
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Stage 5N-10: SEZ tax-treatment card. Renders only when the
            customer declared SEZ at signup. Independent of
            account_status — an SEZ customer can be active OR
            pending_approval and still have their SEZ status flipped.
            Approving SEZ here ONLY affects future renewal invoices;
            already-issued invoices are immutable (snapshot pattern). */}
        {account.customerTaxType && account.customerTaxType !== 'regular' ? (
          <Card title="SEZ tax treatment" size="small" className="md:col-span-2">
            <Descriptions column={2} size="small" colon>
              <Descriptions.Item label="Customer type">
                {(() => {
                  if (account.customerTaxType === 'sez_unit') return 'SEZ Unit';
                  if (account.customerTaxType === 'sez_developer') return 'SEZ Developer';
                  return account.customerTaxType;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Verification status">
                {(() => {
                  const s = account.sezVerificationStatus;
                  const color = ({
                    pending:        'gold',
                    approved:       'green',
                    rejected:       'red',
                    not_applicable: 'default'
                  })[s] || 'default';
                  return <Tag color={color}>{s || 'not_applicable'}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Authorised operations">
                {account.sezAuthorisedOperationsConfirmed ? (
                  <Tag color="green">Confirmed</Tag>
                ) : (
                  <Tag color="red">Not confirmed</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="LOA / approval number">
                {account.sezApprovalNumber
                  ? <code className="text-xs">{account.sezApprovalNumber}</code>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Decided at">
                {formatDateTime(account.sezDecidedAt) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Decided by (admin id)">
                {account.sezDecidedBy
                  ? <CopyableText value={account.sezDecidedBy} max={14} />
                  : '—'}
              </Descriptions.Item>
              {account.sezRejectReason ? (
                <Descriptions.Item label="Reject reason" span={2}>
                  {account.sezRejectReason}
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            {account.sezVerificationStatus === 'pending' ||
            account.sezVerificationStatus === 'rejected' ||
            account.sezVerificationStatus === 'approved' ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {account.sezVerificationStatus !== 'approved' ? (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    disabled={!canApproveSez}
                    onClick={() => setApproveSezOpen(true)}
                  >
                    Approve SEZ status
                  </Button>
                ) : null}
                {account.sezVerificationStatus !== 'rejected' ? (
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    disabled={!canRejectSez}
                    onClick={() => {
                      setRejectSezReason('');
                      setRejectSezOpen(true);
                    }}
                  >
                    Reject SEZ status
                  </Button>
                ) : null}
              </div>
            ) : null}

            <Alert
              type="info"
              showIcon
              className="mt-4"
              message="Phase 1: zero-rated billing applies only to future renewal invoices issued AFTER SEZ approval. The original signup invoice stays at regular GST."
            />
          </Card>
        ) : null}

        {/* Stage 5N-7: subscription lifecycle. Renders only when the
            tenant has a subscription row — pending_approval tenants
            without a provisioned subscription will simply not see this
            card. The effective status is computed server-side using
            saas_billing_settings.grace_period_days, so what the
            operator sees here is exactly what login enforcement uses. */}
        {account.subscriptionLifecycle ? (
          <Card title="Subscription lifecycle" size="small" className="md:col-span-2">
            <Descriptions column={2} size="small" colon>
              <Descriptions.Item label="Effective status">
                {(() => {
                  const s = account.subscriptionLifecycle.effectiveStatus;
                  const color = ({
                    active:    'green',
                    reminder:  'gold',
                    grace:     'orange',
                    suspended: 'red'
                  })[s] || 'default';
                  return <Tag color={color}>{s}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Stored status">
                <code className="text-xs">
                  {account.subscriptionLifecycle.storedStatus || '—'}
                </code>
              </Descriptions.Item>
              <Descriptions.Item label="Access allowed">
                {account.subscriptionLifecycle.isAccessAllowed ? (
                  <Tag color="green">yes</Tag>
                ) : (
                  <Tag color="red">no</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Reason">
                {account.subscriptionLifecycle.reason
                  ? <code className="text-xs">{account.subscriptionLifecycle.reason}</code>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Period start">
                {formatDateTime(account.subscriptionLifecycle.currentPeriodStart) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Period end">
                {formatDateTime(account.subscriptionLifecycle.currentPeriodEnd) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Grace ends at">
                {formatDateTime(account.subscriptionLifecycle.graceEndsAt) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Next billing at">
                {formatDateTime(account.subscriptionLifecycle.nextBillingAt) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Days remaining">
                {Number.isInteger(account.subscriptionLifecycle.daysRemaining)
                  ? account.subscriptionLifecycle.daysRemaining
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Grace days remaining">
                {Number.isInteger(account.subscriptionLifecycle.graceDaysRemaining)
                  ? account.subscriptionLifecycle.graceDaysRemaining
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Free months">
                {account.subscriptionLifecycle.freeMonths || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Cancelled at">
                {formatDateTime(account.subscriptionLifecycle.cancelledAt) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Last computed">
                {formatDateTime(account.subscriptionLifecycle.lastEffectiveStatusAt) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Last computed status">
                {account.subscriptionLifecycle.lastEffectiveStatus
                  ? <code className="text-xs">{account.subscriptionLifecycle.lastEffectiveStatus}</code>
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}

        <Card title="Approval status" size="small" className="md:col-span-2">
          <Descriptions column={2} size="small" colon>
            <Descriptions.Item label="Account status">
              <AccountStatusTag status={status} />
            </Descriptions.Item>
            <Descriptions.Item label="Approval required">
              {account.approvalRequired ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Created at">
              {formatDateTime(account.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Lifecycle status">
              {account.lifecycleStatus || '—'}
            </Descriptions.Item>
            {account.approvedAt ? (
              <Descriptions.Item label="Approved at">
                {formatDateTime(account.approvedAt)}
              </Descriptions.Item>
            ) : null}
            {account.approvedBy ? (
              <Descriptions.Item label="Approved by (admin id)">
                <CopyableText value={account.approvedBy} max={14} />
              </Descriptions.Item>
            ) : null}
            {account.rejectedAt ? (
              <Descriptions.Item label="Rejected at">
                {formatDateTime(account.rejectedAt)}
              </Descriptions.Item>
            ) : null}
            {account.rejectedBy ? (
              <Descriptions.Item label="Rejected by (admin id)">
                <CopyableText value={account.rejectedBy} max={14} />
              </Descriptions.Item>
            ) : null}
            {account.rejectReason ? (
              <Descriptions.Item label="Reject reason" span={2}>
                {account.rejectReason}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          {status === 'rejected' ? (
            <Alert
              type="info"
              showIcon
              className="mt-4"
              message="Rejected accounts cannot be reactivated from this surface."
              description="The customer must complete a fresh signup."
            />
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            {status === 'pending_approval' ? (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  disabled={!canApprove}
                  onClick={() => setApproveOpen(true)}
                >
                  Approve
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  disabled={!canReject}
                  onClick={() => {
                    setRejectReason('');
                    setRejectOpen(true);
                  }}
                >
                  Reject
                </Button>
              </>
            ) : null}

            {status === 'active' ? (
              <Button
                danger
                icon={<PauseCircleOutlined />}
                disabled={!canSuspend}
                onClick={() => {
                  setSuspendReason('');
                  setSuspendOpen(true);
                }}
              >
                Suspend
              </Button>
            ) : null}

            {status === 'suspended' ? (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                disabled={!canReactivate}
                onClick={() => setReactivateOpen(true)}
              >
                Reactivate
              </Button>
            ) : null}
          </div>
        </Card>
      </div>

      <Modal
        open={approveOpen}
        title="Approve account?"
        okText="Approve account"
        okButtonProps={{ type: 'primary', loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={doApprove}
        onCancel={() => (busy ? null : setApproveOpen(false))}
        maskClosable={!busy}
      >
        <p className="m-0">
          This will activate <strong>{account.legalName || account.slug}</strong> and
          allow the master user to log in.
        </p>
      </Modal>

      <Modal
        open={rejectOpen}
        title="Reject account?"
        okText="Reject account"
        okButtonProps={{
          danger: true,
          loading: busy,
          disabled: rejectReason.trim().length === 0
        }}
        cancelButtonProps={{ disabled: busy }}
        onOk={doReject}
        onCancel={() => {
          if (busy) return;
          setRejectOpen(false);
          setRejectReason('');
        }}
        maskClosable={!busy}
        destroyOnHidden
      >
        <p className="m-0 mb-2">
          Marking <strong>{account.legalName || account.slug}</strong> as rejected
          will block sign-in and surface the reason in the audit trail.
        </p>
        <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Reason (required)
        </label>
        <TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="e.g. GSTIN does not match supplied address"
          disabled={busy}
        />
      </Modal>

      <Modal
        open={suspendOpen}
        title="Suspend account?"
        okText="Suspend account"
        okButtonProps={{ danger: true, loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={doSuspend}
        onCancel={() => {
          if (busy) return;
          setSuspendOpen(false);
          setSuspendReason('');
        }}
        maskClosable={!busy}
        destroyOnHidden
      >
        <p className="m-0 mb-2">
          Suspending <strong>{account.legalName || account.slug}</strong> will block
          sign-in immediately. Reactivate from this same screen when ready.
        </p>
        <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Reason (optional)
        </label>
        <TextArea
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Optional context for the audit trail"
          disabled={busy}
        />
      </Modal>

      <Modal
        open={reactivateOpen}
        title="Reactivate account?"
        okText="Reactivate account"
        okButtonProps={{ type: 'primary', loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={doReactivate}
        onCancel={() => (busy ? null : setReactivateOpen(false))}
        maskClosable={!busy}
      >
        <p className="m-0">
          Reactivating <strong>{account.legalName || account.slug}</strong> will let
          the master user sign in again.
        </p>
      </Modal>

      {/* Stage 5N-10: SEZ approval modals */}
      <Modal
        open={approveSezOpen}
        title="Approve SEZ status?"
        okText="Approve SEZ"
        okButtonProps={{ type: 'primary', loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={doApproveSez}
        onCancel={() => (busy ? null : setApproveSezOpen(false))}
        maskClosable={!busy}
      >
        <p className="m-0">
          This marks <strong>{account.legalName || account.slug}</strong> as an
          approved SEZ {account.customerTaxType === 'sez_developer' ? 'Developer' : 'Unit'}.
        </p>
        <p className="m-0 mt-2 text-ink-500 text-sm">
          Future renewal invoices will be issued as zero-rated supplies.
          The original signup invoice and any prior invoices stay unchanged.
          Verify the LOA / approval number against the customer’s document
          before approving.
        </p>
      </Modal>

      <Modal
        open={rejectSezOpen}
        title="Reject SEZ status?"
        okText="Reject SEZ"
        okButtonProps={{
          danger: true,
          loading: busy,
          disabled: rejectSezReason.trim().length === 0
        }}
        cancelButtonProps={{ disabled: busy }}
        onOk={doRejectSez}
        onCancel={() => {
          if (busy) return;
          setRejectSezOpen(false);
          setRejectSezReason('');
        }}
        maskClosable={!busy}
        destroyOnHidden
      >
        <p className="m-0 mb-2">
          Rejecting SEZ status for <strong>{account.legalName || account.slug}</strong>
          {' '}means future renewal invoices stay at regular GST. The customer
          will be notified of the reason.
        </p>
        <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Reason (required)
        </label>
        <TextArea
          value={rejectSezReason}
          onChange={(e) => setRejectSezReason(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="e.g. LOA number does not match supplied document"
          disabled={busy}
        />
      </Modal>
    </div>
  );
}
